/* eslint-disable camelcase */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Modal, Switch } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import NetworkContext from '@totem/components/devices/network/networkContext';
import { FormatBytes } from '@totem/components/devices/network/utilities';
import { NetworkInterface } from '@totem/components/networkConcerns/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

import ethernet_down from './ethernet-down.svg';
import ethernet_unknown from './ethernet-unknown.svg';
import ethernet_up from './ethernet-up.svg';
import generic_down from './generic-down.svg';
import generic_unknown from './generic-unknown.svg';
import generic_up from './generic-up.svg';
import wifi_down from './wifi-down.svg';
import wifi_unknown from './wifi-unknown.svg';
import wifi_up from './wifi-up.svg';

const NetworkTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { data, loading, onAction, deviceId } = useContext(NetworkContext);
  const [isSending, setIsSending] = useState<boolean>(false);

  const canApproveInterfaces = useMemo(() => {
    return permissions.internalOrganizationalAdministrator.hasPermissions(
      userProfile,
    );
  }, [userProfile]);

  const getData = () => {
    if (typeof data !== 'undefined' && data !== null) {
      return data;
    }
    return [];
  };

  const getStatus = (record: NetworkInterface) => {
    if (
      record.details.interfaceType.toLowerCase().indexOf('ethernet') >= 0 ||
      record.details.interfaceType.toLowerCase().indexOf('802.3') >= 0
    ) {
      // Ethernet
      switch (record.details.status.toLowerCase()) {
        case 'up':
          return (
            <img
              width={16}
              src={ethernet_up}
              alt="Ethernet"
              title="Ethernet - Up"
            />
          );
        case 'down':
          return (
            <img
              width={16}
              src={ethernet_down}
              alt="Ethernet"
              title="Ethernet - Down"
            />
          );
        default:
          return <img width={16} src={ethernet_unknown} alt="Ethernet" />;
      }
    }
    if (
      record.details.interfaceType.toLowerCase().indexOf('wifi') >= 0 ||
      record.details.interfaceType.toLowerCase().indexOf('802.11') >= 0
    ) {
      // Wifi
      switch (record.details.status.toLowerCase()) {
        case 'up':
          return <img width={16} src={wifi_up} alt="Wifi" title="Wifi - Up" />;
        case 'down':
          return (
            <img width={16} src={wifi_down} alt="Wifi" title="Wifi - Down" />
          );
        default:
          return <img width={16} src={wifi_unknown} alt="Wifi" />;
      }
    }

    switch (record.details.status.toLowerCase()) {
      case 'up':
        return (
          <img width={16} src={generic_up} alt="Adapter" title="Adapter - Up" />
        );
      case 'down':
        return (
          <img
            width={16}
            src={generic_down}
            alt="Adapter"
            title="Adapter - Down"
          />
        );
      default:
        return <img width={16} src={generic_unknown} alt="Adapter" />;
    }
  };

  const sendApprovalUpdate = useCallback(
    async (selectedDeviceId: string, macAddress: string, approval: boolean) => {
      if (isSending) {
        return;
      }
      setIsSending(true);

      const endPointAction = approval ? 'approve' : 'revoke';

      fetch(
        `${DEVICES_ENDPOINT}/${selectedDeviceId}/networkInterface/${macAddress}/${endPointAction}`,
        {
          method: 'PUT',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        },
      ).then((res) => {
        CheckResponseShowError(res);

        setIsSending(false);

        onAction('refresh', null);
      });
    },
    [isSending],
  );

  const updateApproval = (
    selectedDeviceId: string,
    macAddress: string,
    approval: boolean,
  ) => {
    const action = approval
      ? 'approve the interface'
      : 'revoke approval for the interface';
    const okText = approval ? 'Approve' : 'Revoke';
    Modal.confirm({
      title: 'Are you sure?',
      content: `Please confirm that you would like to ${action}.`,
      okText,
      okType: 'danger',
      cancelText: 'Cancel',
      open: false,
      onOk: () => sendApprovalUpdate(selectedDeviceId, macAddress, approval),
    });
  };

  const columns: ColumnProps<NetworkInterface>[] = [
    {
      title: 'Interface Name',
      dataIndex: 'interfaceName',
      key: 'interfaceName',
      defaultSortOrder: 'ascend',
      render: (_, record: NetworkInterface) => record.details.interfaceName,
      sorter: (compA, compB) =>
        compA.details.interfaceName === compB.details.interfaceName
          ? 0
          : compA.details.interfaceName > compB.details.interfaceName
            ? 1
            : -1,
    },
    {
      title: 'Interface Type',
      dataIndex: 'interfaceType',
      key: 'interfaceType',
      render: (_, record: NetworkInterface) => record.details.interfaceType,
      sorter: (compA, compB) =>
        compA.details.interfaceType === compB.details.interfaceType
          ? 0
          : compA.details.interfaceType > compB.details.interfaceType
            ? 1
            : -1,
    },
    {
      title: 'DNS Hostname',
      dataIndex: 'dnsHostName',
      key: 'dnsHostName',
      render: (_, record: NetworkInterface) => record.details.dnsHostName,
      sorter: (compA, compB) =>
        compA.details.dnsHostName === compB.details.dnsHostName
          ? 0
          : compA.details.dnsHostName > compB.details.dnsHostName
            ? 1
            : -1,
    },
    {
      title: 'DNS Servers',
      dataIndex: 'dnsServers',
      key: 'dnsServers',
      render: (_, record: NetworkInterface) => record.details.dnsServers,
      sorter: (compA, compB) =>
        compA.details.dnsServers === compB.details.dnsServers
          ? 0
          : compA.details.dnsServers > compB.details.dnsServers
            ? 1
            : -1,
    },
    {
      title: 'Link Speed',
      dataIndex: 'linkSpeed',
      key: 'linkSpeed',
      render: (_, record: NetworkInterface) =>
        FormatBytes(+record.details.linkSpeed),
      sorter: (compA, compB) =>
        compA.details.linkSpeed === compB.details.linkSpeed
          ? 0
          : compA.details.linkSpeed > compB.details.linkSpeed
            ? 1
            : -1,
    },
    {
      title: 'MAC Addresses',
      dataIndex: 'macAddress',
      key: 'macAddress',
      render: (_, record: NetworkInterface) => {
        return record.details.macAddress.map((rec) => (
          <div key={rec}>{rec}</div>
        ));
      },
    },
    {
      title: 'IP Addresses',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (_, record: NetworkInterface) => {
        return record.details.ipAddress.map((rec) => (
          <div key={rec}>{rec}</div>
        ));
      },
    },
    {
      title: 'Subnet Mask',
      dataIndex: 'subnetMask',
      key: 'subnetMask',
      render: (_, record: NetworkInterface) => record.details.subnetMask,
      sorter: (compA, compB) =>
        compA.details.subnetMask === compB.details.subnetMask
          ? 0
          : compA.details.subnetMask > compB.details.subnetMask
            ? 1
            : -1,
    },
    {
      title: 'Default Gateway',
      dataIndex: 'defaultGateway',
      key: 'defaultGateway',
      render: (_, record: NetworkInterface) => record.details.defaultGateway,
      sorter: (compA, compB) =>
        compA.details.defaultGateway === compB.details.defaultGateway
          ? 0
          : compA.details.defaultGateway > compB.details.defaultGateway
            ? 1
            : -1,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record: NetworkInterface) => getStatus(record),
      sorter: (compA, compB) =>
        compA.details.status === compB.details.status
          ? 0
          : compA.details.status > compB.details.status
            ? 1
            : -1,
    },
    {
      title: 'Approved',
      dataIndex: 'interfaces.approved',
      key: 'interfaces.approved',
      showSorterTooltip: true,
      render: (_, record: NetworkInterface) => (
        <>
          <div>
            <Switch
              value={record.approved}
              disabled={!canApproveInterfaces}
              style={{
                backgroundColor: record.approved
                  ? colors.event.criticality.background.ok
                  : colors.event.criticality.background.critical,
              }}
              onChange={(newValue) =>
                updateApproval(deviceId, record.macAddress, newValue)
              }
            />
          </div>
        </>
      ),
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getData()}
      loading={loading}
      rowKey={(record) => record.macAddress}
    />
  );
};

export default NetworkTable;
